import * as React from 'react';
import { Dropdown } from 'react-bootstrap';
import styled, { css } from 'styled-components';
import clsx from 'clsx';
import { SvgImage } from '../SvgImage';
import { getAssetPath } from '../../util/images';
import {
  devicesConnectionsLedColors,
  ledBrightnessPercentConvert
} from '../../util';
import { ColorSlider } from '../ColorSlider/ColorSlider';
import { NoneButton } from '../AttachmentTag';
import { Text } from '../Text/Text';
import getColorByName from './GetColorByName';
import { IColorSelectDropdown } from './ColorSelectDropdown.types';
import {
  isBuildInColor,
  isColorCircleActive
} from './ColorSelectDropdown.logic';

export const noColorCircleMixin = () => css`
  &:before {
    position: absolute;
    content: '';
    left: 0;
    top: 50%;
    right: 0;
    border-top: 1px solid gray;

    -webkit-transform: rotate(-45deg);
    -moz-transform: rotate(-45deg);
    -ms-transform: rotate(-45deg);
    -o-transform: rotate(-45deg);
    transform: rotate(-45deg);
  }
`;

const DropdownMenu = styled(Dropdown.Menu)`
  padding: 16px 8px;
`;

const SelectedColorDropdown = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 10px;
  //TODO find proper equivalent on the current colors pallete
  background-color: var(--background-3);
  border-radius: 8px;
  width: 162px;
  color: var(--fonts-1);
  svg path {
    stroke: ${({ theme }) => theme.colors.font1};
  }
`;

const SelectedColorContentWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const SelectedColorCircle = styled.div<{ color: any }>`
  position: relative;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  margin-right: 8px;
  border: 1px solid gray;
  box-shadow: 0 0 4px rgba(0, 0, 0, 0.15);
  ${({ color }) => {
    if (!color) {
      return noColorCircleMixin();
    }
    return css`
      background-color: ${() => `hsl(${color.hue}, ${color.saturation}%, 50%)`};
    `;
  }}
`;

const ColorsCirclesWrapper = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: center;
  width: 184px;
  margin-bottom: 8px;
`;

const ColorsCircleButton = styled(NoneButton)<{ active: boolean }>`
  margin-bottom: 8px;
  padding: 4px;
  border: 4px solid transparent;
  ${({ active }) =>
    active &&
    css`
      border: 4px solid white;
      border-radius: 50%;
      box-shadow: 0 0 4px rgba(0, 0, 0, 0.15),
        inset 0 1px 2px rgba(255, 255, 255, 0.25);
    `}
`;

const ColorsCircle = styled.div<{ colorName: string }>`
  position: relative;
  width: 32px;
  height: 32px;
  border: 1px solid;
  border-radius: 50%;
  background-color: ${({ colorName }) => getColorByName(colorName).bgColor};
  border-color: ${({ colorName }) => getColorByName(colorName).bColor};
  ${({ colorName }) => isBuildInColor(colorName) && noColorCircleMixin()}
`;

const ColorSliderWrapper = styled.div`
  height: 48px;
  width: 100%;
  display: flex;
  align-items: center;
  padding: 0 16px;
  border-radius: 8px;
`;

export const CustomToggle = React.forwardRef((buttonProps, buttonRef) => {
  // @ts-ignore
  const { onClick, frameLedSelectedColors, color } = buttonProps;
  const { Color, Brightness } = color;

  return (
    <NoneButton
      type="button"
      data-testid="select-color-dropdown-toggle"
      onClick={e => {
        e.preventDefault();
        onClick(e);
      }}
      // @ts-ignore
      ref={buttonRef}
    >
      <SelectedColorDropdown>
        <SelectedColorContentWrapper>
          {frameLedSelectedColors.length === 1 && (
            <SelectedColorCircle color={Color} />
          )}
          <Text fontSize="12px">
            {frameLedSelectedColors.length === 1
              ? `${ledBrightnessPercentConvert(Brightness) || 0}%`
              : 'More than 1 color'}
          </Text>
        </SelectedColorContentWrapper>
        <SvgImage inAssetsPath={getAssetPath('icons/table-arrow-down.svg')} />
      </SelectedColorDropdown>
    </NoneButton>
  );
});

const ColorSelectDropdown = (props: IColorSelectDropdown) => {
  const { frameLedSelectedColors, defaultColor, onFrameLedColorSelect } = props;

  return (
    <Dropdown data-testid="select-color-dropdown">
      <Dropdown.Toggle
        id="filters-button"
        // @ts-ignore
        color={defaultColor}
        frameLedSelectedColors={frameLedSelectedColors}
        as={CustomToggle}
      />
      <DropdownMenu>
        <ColorsCirclesWrapper>
          {Object.keys(devicesConnectionsLedColors).map(ledColorName => {
            const isActive = isColorCircleActive(ledColorName, defaultColor);

            return (
              <ColorsCircleButton
                data-testId={`color-circle-${ledColorName}`}
                className={clsx({
                  active: isActive
                })}
                type="button"
                key={`colorCircle${ledColorName}`}
                active={isActive}
                onClick={() => {
                  if (onFrameLedColorSelect) {
                    onFrameLedColorSelect({
                      Brightness: 0,
                      Color: devicesConnectionsLedColors[ledColorName]
                    });
                  }
                }}
              >
                <ColorsCircle colorName={ledColorName} />
              </ColorsCircleButton>
            );
          })}
        </ColorsCirclesWrapper>
        <ColorSliderWrapper>
          {defaultColor.Color && (
            <ColorSlider
              color={defaultColor}
              onColorUpdate={Brightness => {
                if (onFrameLedColorSelect) {
                  onFrameLedColorSelect({ ...defaultColor, Brightness });
                }
              }}
            />
          )}
        </ColorSliderWrapper>
      </DropdownMenu>
    </Dropdown>
  );
};

export default ColorSelectDropdown;
